import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Home as HomeIcon, HeartHandshake, ShieldCheck, Clock, MapPin } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { ZoomableImage } from "@/components/Lightbox";
import { Pillars, TAGLINE } from "@/components/Pillars";
import { useFemaleHome, femaleHomeStatus } from "@/hooks/useFemaleHome";

const HERO_IMG = "https://images.unsplash.com/photo-1588099246635-d6868ab08b44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxnb2xkZW4lMjBzdWJ1cmJhbiUyMGhvdXNlJTIwZXh0ZXJpb3IlMjB3YXJtJTIwc3VubGlnaHR8ZW58MHx8fHwxNzkxMzkxOTY5fDA&ixlib=rb-4.1.0&q=85";
const MANOR_IMG = "/images/manor-house.jpg";
const JALIYAH_IMG = "/images/jaliyah-red-dress.jpg";

const stats = [
  { icon: Clock, label: "24/7 Staffed Care" },
  { icon: ShieldCheck, label: "ASAM 3.3 Level" },
  { icon: HomeIcon, label: "Male & Female Homes" },
  { icon: HeartHandshake, label: "Trauma-Informed" },
];

export default function Home() {
  const heroRef = useRef(null);
  const fh = useFemaleHome();
  const fhStatus = femaleHomeStatus(fh);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  return (
    <div data-testid="home-page">
      <section ref={heroRef} data-testid="hero-section" className="relative overflow-hidden bg-[#FAF8F5] grain">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#F3E8FF] blur-3xl opacity-70" />
        <div className="absolute top-40 -left-32 w-[360px] h-[360px] rounded-full bg-[#FEF3C7] blur-3xl opacity-70" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-2 gap-14 items-center relative">
          <motion.div style={{ y: textY }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="mb-6"
            >
              <img src="/logo.png" alt="Jaliyah's House of Hope" className="h-24 sm:h-28 w-auto float-soft" data-testid="hero-logo" />
            </motion.div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-4" data-testid="hero-eyebrow">
              ASAM 3.3 SUD Residential Group Homes · Virginia
            </p>
            <MaskedLines
              className="font-display font-black text-[#2E1065] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              lines={[
                "Safe Spaces.",
                "Stronger",
                "Tomorrows.",
              ]}
            />
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="mt-5 font-display italic text-xl sm:text-2xl text-[#B45309]"
              data-testid="hero-tagline"
            >
              {TAGLINE}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              className="mt-4 text-base sm:text-lg text-slate-700 leading-relaxed max-w-xl"
              data-testid="hero-subtext"
            >
              Founded by a father in honor of his daughter Jaliyah, our umbrella of homes offers 24/7
              clinically managed, high-intensity residential recovery for men and women ready to rebuild —
              so her name keeps changing lives, the way she always did.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-referral-cta"
                className="group inline-flex items-center gap-2 bg-[#5B21B6] hover:bg-[#2E1065] text-white font-bold px-7 py-3.5 rounded-full shadow-lg shadow-purple-500/25 transition-colors duration-300"
              >
                Start a Referral
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/story"
                data-testid="hero-story-cta"
                className="inline-flex items-center gap-2 border-2 border-[#5B21B6] text-[#5B21B6] hover:bg-[#5B21B6] hover:text-white font-bold px-7 py-3.5 rounded-full transition-colors duration-300"
              >
                Jaliyah's Story
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-10 flex flex-wrap gap-3"
              data-testid="hero-stat-badges"
            >
              {stats.map((s) => (
                <span
                  key={s.label}
                  className="inline-flex items-center gap-2 bg-white border border-purple-100 rounded-full px-4 py-2 text-xs font-bold text-[#2E1065] shadow-sm"
                >
                  <s.icon size={14} className="text-[#F59E0B]" />
                  {s.label}
                </span>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-br from-[#5B21B6] to-[#F59E0B] rounded-[2.5rem] rotate-2 opacity-90" />
            <div className="relative rounded-[2rem] overflow-hidden h-[420px] sm:h-[520px] shadow-2xl">
              <motion.img
                src={HERO_IMG}
                alt="A warm home bathed in golden sunrise light"
                style={{ y: imgY, scale: 1.15 }}
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2E1065]/50 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/85 backdrop-blur-md rounded-2xl px-5 py-4 flex items-center gap-3">
                <MapPin className="text-[#5B21B6] shrink-0" size={22} />
                <p className="text-sm font-bold text-[#2E1065]">
                  The first Male Manor is now welcoming residents in Petersburg, VA
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      <section data-testid="home-story-teaser" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-3 border-4 border-amber-400 rounded-[2rem] -rotate-1" />
            <ZoomableImage
              src={JALIYAH_IMG}
              alt="Jaliyah Ikja Mason, whose name and light this mission carries"
              className="relative rounded-[2rem] shadow-xl"
              imgClassName="w-full h-[420px] object-cover object-top"
              testId="story-teaser-image"
            />
            <div className="absolute -bottom-5 left-5 bg-white rounded-2xl shadow-xl px-5 py-3 border border-purple-100">
              <p className="font-display font-bold text-[#2E1065] text-sm">Jaliyah Ikja Mason</p>
              <p className="text-[11px] text-slate-500">2010 — 2026 · Forever our butterfly</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-3">Why We Exist</p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1E1B4B] leading-tight" data-testid="story-teaser-heading">
            One daughter's light became a house full of hope.
          </h2>
          <p className="mt-5 text-base text-slate-700 leading-relaxed">
            Jaliyah lived fifteen fabulous years — a rising 10th grader dreaming of becoming a NICU nurse, a
            devoted daughter, sister, and friend whose courageous smile lit up every room. When God called her
            home, her father, co-owner Davonte Mason, made a promise: her name would live on by changing the
            lives of others. These homes are that promise — safe, structured places where people fighting
            substance use disorder are met with the same love she gave so freely.
          </p>
          <Link
            to="/story"
            data-testid="story-teaser-link"
            className="mt-7 inline-flex items-center gap-2 text-[#5B21B6] font-bold group"
          >
            Read her story
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      <Pillars />

      <section data-testid="home-homes-preview" className="bg-[#F4EFE6] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-3">One Umbrella, Many Homes</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1E1B4B]" data-testid="homes-preview-heading">
              Under Jaliyah's name, every home is a sanctuary.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 gap-8">
            <Reveal>
              <Link to="/homes" data-testid="male-manor-card" className="group block bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 border border-purple-100">
                <div className="h-64 overflow-hidden">
                  <img src={MANOR_IMG} alt="The Male Manor in Petersburg, Virginia" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-7">
                  <span className="inline-block bg-emerald-100 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full mb-3">Now Accepting Residents</span>
                  <h3 className="font-display font-bold text-2xl text-[#2E1065]">The Male Manor</h3>
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5"><MapPin size={14} className="text-[#F59E0B]" /> 1115 Rome St, Petersburg, VA 23803</p>
                  <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                    Our flagship ASAM 3.3 residence — 24/7 structured, high-intensity care built for men in recovery.
                  </p>
                </div>
              </Link>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/homes" data-testid="female-home-card" className="group block bg-gradient-to-br from-[#5B21B6] to-[#2E1065] rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500 h-full">
                <div className="p-7 h-full flex flex-col justify-center min-h-[380px] relative grain">
                  <span data-testid="home-female-status" className={`inline-block self-start text-xs font-bold px-3 py-1 rounded-full mb-3 ${fhStatus.tone === "live" ? "bg-emerald-500 text-white" : "bg-amber-100 text-amber-700"}`}>{fhStatus.label}</span>
                  <h3 className="font-display font-bold text-2xl text-white">The Female Home</h3>
                  <p className="text-sm text-purple-200 mt-1" data-testid="home-female-location">
                    {fh.announced && fh.location ? fh.location : "Location to be announced, Virginia"}
                  </p>
                  <p className="mt-3 text-sm text-purple-100 leading-relaxed max-w-sm">
                    {fh.announced && fh.note
                      ? fh.note
                      : "The umbrella is opening wider. A dedicated ASAM 3.3 residence for women is on the way — same dignity, same structure, same hope."}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-amber-400 font-bold text-sm">
                    Follow our progress <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="home-cta-band" className="relative bg-[#2E1065] py-20 lg:py-24 overflow-hidden grain">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative">
          <Reveal>
            <h2 className="font-display font-black text-3xl sm:text-5xl text-[#FAF8F5] leading-tight" data-testid="cta-band-heading">
              Recovery deserves a home, not just a bed.
            </h2>
            <p className="mt-5 text-purple-200 text-base sm:text-lg max-w-2xl mx-auto">
              Whether you are seeking help, referring a client, or standing with a loved one — the door is open.
            </p>
            <Link
              to="/contact"
              data-testid="cta-band-button"
              className="mt-9 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-9 py-4 rounded-full shadow-xl shadow-amber-500/20 transition-colors duration-300"
            >
              Refer Someone Today <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
